/* ============================================================================
   UI / actions — buttons.
   Button (text, optional icons), ButtonLink (a link that looks like a Button), ExternalLink and AppLink (links whose address is checked), PlainButton (a real button that draws itself), IconButton (square, icon only), FileButton (opens the file chooser).
   ============================================================================ */
import React from "react";
import { safeAppRoute } from "../Api/safeAppRoute.js";
import { safeHttpUrl } from "../Api/safeHttpUrl.js";
import { cx } from "./util.js";

const buttonClass = (variant, size, className) => cx("ds-btn", variant && `ds-btn--${variant}`, size && `ds-btn--${size}`, className);

/* The one button for doing something here: `variant` is primary, danger, ghost, link, text or success (none is the plain outlined one), `size` sm, `leftIcon` and `rightIcon` for a mark beside the words. */
export const Button = React.forwardRef(function Button({ variant, size, leftIcon, rightIcon, className, children, ...props }, ref) {
  return (
    <button ref={ref} className={buttonClass(variant, size, className)} {...props}>
      {leftIcon}{children}{rightIcon}
    </button>
  );
});

/* A link that looks like a Button: for going to another page (or a document in a new tab) rather than doing something
   here. It takes Button's variant, size and icons, and an <a>'s href, target and rel. */
export const ButtonLink = React.forwardRef(function ButtonLink({ variant, size, leftIcon, rightIcon, className, href, children, ...props }, ref) {
  // Only an http(s) address or a page of this app is followed; anything else is drawn as the same button with no link.
  const target = safeHttpUrl(href) || safeAppRoute(href);
  const Tag = target ? "a" : "span";
  return (
    <Tag ref={ref} className={buttonClass(variant, size, className)} {...(target ? { href: target, ...props } : {})}>
      {leftIcon}{children}{rightIcon}
    </Tag>
  );
});

/* A link to an address that came from data (a lead's website, a meeting or recording link, a repository). The address
   is typed or imported by people and systems, so it is never trusted: only a real http(s) address becomes a link, and it
   opens in a new tab without handing the page over. Anything else (javascript:, data:, plain text, empty) is shown as
   plain text. Every `<a href={...}>` outside this library is one of these two. */
export const ExternalLink = React.forwardRef(function ExternalLink({ href, children, ...props }, ref) {
  const safe = safeHttpUrl(href);
  if (!safe) return <span className={props.className} title={props.title}>{children}</span>;
  return <a ref={ref} href={safe} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
});

/* A link to a page of this app. `to` must be an address on this site (one slash, then a path); anything else is shown as
   plain text. With `onNavigate`, a plain click moves inside the app without a reload, and a modified click (new tab,
   new window) is left to the browser. */
export const AppLink = React.forwardRef(function AppLink({ to, onNavigate, onClick, children, ...props }, ref) {
  const route = safeAppRoute(to);
  if (!route) return <span className={props.className} title={props.title}>{children}</span>;
  const go = (event) => {
    onClick?.(event);
    if (!onNavigate || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onNavigate(route);
  };
  return <a ref={ref} href={route} onClick={go} {...props}>{children}</a>;
});

/* A button with no look of its own, for a control that draws itself (a chip, a pill, a clickable card, a heat-map cell):
   it is still a real button, so it is reached by keyboard and read as one. Give it a class for its look. */
export const PlainButton = React.forwardRef(function PlainButton({ type = "button", ...props }, ref) {
  return <button ref={ref} type={type} {...props} />;
});

/* A square button with only an icon. `label` is its name for screen readers and its hover text; `active` marks it
   as the one that is on (a panel it opens is showing). */
export const IconButton = React.forwardRef(function IconButton({ label, active = false, className, children, ...props }, ref) {
  return (
    <button ref={ref} type="button" className={cx("ds-icon-btn", active && "is-active", className)} aria-label={label} title={label} {...props}>
      {children}
    </button>
  );
});

/* A button that opens the file chooser and hands the chosen file to `onFile(file)` (with `multiple`, the list of files).
   It takes everything Button takes (variant, size, leftIcon, aria-label, title); `inputRef` reaches the hidden field.
   The field is emptied afterwards, so choosing the same file again still counts. */
export const FileButton = React.forwardRef(function FileButton({ accept, onFile, multiple = false, disabled, inputRef, children, ...props }, ref) {
  const input = React.useRef(null);
  const setInput = (node) => {
    input.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) inputRef.current = node;
  };
  const onChange = (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (files.length) onFile?.(multiple ? files : files[0]);
  };
  return (
    <>
      <input ref={setInput} type="file" hidden accept={accept} multiple={multiple} disabled={disabled} onChange={onChange} />
      <Button ref={ref} type="button" disabled={disabled} onClick={() => input.current?.click()} {...props}>{children}</Button>
    </>
  );
});
